import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { WAIVER, WAIVER_TEXT } from "@/content/waiver";

// ── Types ────────────────────────────────────────────────────────────────────────────────────

export type Athlete = {
  id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  date_of_birth: string | null;
  grade: number;
  status: "active" | "inactive";
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
};

export type AthleteInput = Omit<Athlete, "id" | "status">;

export type Program = {
  id: string;
  slug: string;
  name: string;
  short_description: string | null;
  season: string | null;
  starts_on: string | null;
  ends_on: string | null;
  price_cents: number;
  grade_min: number;
  grade_max: number;
  is_active: boolean;
  display_order: number;
};

export type Enrollment = {
  id: string;
  program_id: string;
  athlete_id: string;
  status: "pending" | "paid" | "cancelled" | "refunded";
  price_cents: number;
  created_at: string;
};

const ATHLETE_COLUMNS =
  "id, first_name, last_name, preferred_name, date_of_birth, grade, status, emergency_contact_name, emergency_contact_phone";

// ── Queries ──────────────────────────────────────────────────────────────────────────────────

export const athletesQuery = queryOptions({
  queryKey: ["athletes"],
  queryFn: async (): Promise<Athlete[]> => {
    const { data, error } = await supabase
      .from("athletes")
      .select(ATHLETE_COLUMNS)
      .eq("status", "active")
      .order("first_name");
    if (error) throw error;
    return data as Athlete[];
  },
});

export const athleteQuery = (id: string) =>
  queryOptions({
    queryKey: ["athletes", id],
    queryFn: async (): Promise<Athlete | null> => {
      const { data, error } = await supabase.from("athletes").select(ATHLETE_COLUMNS).eq("id", id).maybeSingle();
      if (error) throw error;
      return data as Athlete | null;
    },
  });

export const programsQuery = queryOptions({
  queryKey: ["programs"],
  queryFn: async (): Promise<Program[]> => {
    const { data, error } = await supabase
      .from("programs")
      .select("id, slug, name, short_description, season, starts_on, ends_on, price_cents, grade_min, grade_max, is_active, display_order")
      .eq("is_public", true)
      .order("display_order");
    if (error) throw error;
    return data as Program[];
  },
});

export const enrollmentsQuery = queryOptions({
  queryKey: ["enrollments"],
  queryFn: async (): Promise<Enrollment[]> => {
    const { data, error } = await supabase
      .from("enrollments")
      .select("id, program_id, athlete_id, status, price_cents, created_at")
      .in("status", ["pending", "paid"])
      .order("created_at");
    if (error) throw error;
    return data as Enrollment[];
  },
});

/** Whether the signed-in parent has signed the current waiver version. */
async function fetchCurrentWaiver(userId: string): Promise<{ id: string; signed_at: string } | null> {
  const { data, error } = await supabase
    .from("waiver_signatures")
    .select("id, signed_at")
    .eq("user_id", userId)
    .eq("waiver_version", WAIVER.version)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Signature of the current agreement version (a new version must be signed again). */
export const waiverQuery = (userId: string) =>
  queryOptions({
    queryKey: ["waiver", userId, WAIVER.version],
    queryFn: () => fetchCurrentWaiver(userId),
  });

export async function hasSignedCurrentWaiver(userId: string): Promise<boolean> {
  return !!(await fetchCurrentWaiver(userId));
}

// ── Mutations ────────────────────────────────────────────────────────────────────────────────

/** Trims text fields and turns empty optional ones into null. */
function cleanAthlete(input: AthleteInput): AthleteInput {
  const opt = (v: string | null) => (v && v.trim() ? v.trim() : null);
  return {
    first_name: input.first_name.trim(),
    last_name: input.last_name.trim(),
    preferred_name: opt(input.preferred_name),
    date_of_birth: input.date_of_birth || null,
    grade: input.grade,
    emergency_contact_name: opt(input.emergency_contact_name),
    emergency_contact_phone: opt(input.emergency_contact_phone),
  };
}

export function useSaveAthlete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: AthleteInput }) => {
      const row = cleanAthlete(input);
      if (id) {
        const { error } = await supabase.from("athletes").update(row).eq("id", id);
        if (error) throw error;
        return id;
      }
      // No .select(): the new row becomes readable once the guardian link exists (trigger).
      const newId = crypto.randomUUID();
      const { error } = await supabase.from("athletes").insert({ id: newId, ...row });
      if (error) throw error;
      return newId;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athletes"] }),
  });
}

export function useSignWaiver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      signedName,
      contact,
    }: {
      signedName: string;
      /** Only when the profile has no phone yet (e.g. Google sign-up). Saved before signing. */
      contact?: { userId: string; phone: string; smsConsent: boolean };
    }) => {
      if (contact) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update({ phone: contact.phone, sms_consent: contact.smsConsent })
          .eq("id", contact.userId);
        if (profileError) throw profileError;
      }
      const { error } = await supabase.from("waiver_signatures").insert({
        waiver_version: WAIVER.version,
        waiver_text: WAIVER_TEXT,
        signed_name: signedName.trim(),
        user_agent: navigator.userAgent.slice(0, 500),
      });
      // Already signed this version (unique per user + version) counts as success.
      if (error && error.code !== "23505") throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waiver"] }),
  });
}

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: cents % 100 ? 2 : 0 })}`;
}
